/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Form, Row, Col, Card, Space, Table, Tabs,
  Input, InputNumber, Button, DatePicker, Select, Typography, message, Flex,
} from "antd";
import { detailColumns, bankColumns as invoiceBankColumns } from "./model";
import { SaveOutlined, ArrowLeftOutlined, FileSearchOutlined, BankFilled } from "@ant-design/icons";
import dayjs from "dayjs";
import InvoiceService from "../../../service/ERP/Invoice.service";
import OptionService from "../../../service/ERP/Options.service";
import ConsigneeService from "../../../service/ERP/Consignee.service";
import ModalJobOrder from "../../../components/modal/ERP/job-order/ModalJobOrder";
import { ModalBanks } from "../../../components/modal/banks/modal-banks";
import ModalPickPI from "../../../components/modal/ERP/proforma/ModalPickPI";
import ModalMapProducts from "../../../components/modal/ERP/proforma/ModalMapProducts";
import { palletWeightOf, saleUnitOf } from "../../../utils/pack-type";
import "./MyPage.css";

const invService = InvoiceService();
const consigneeService = ConsigneeService();
const optionService = OptionService();

const DEFAULT_TERMS = {
  country_of_origin: "THAILAND",
  delivery_term: "EXW NINE STAR FOOD FACILITY, THAILAND",
  payment_term: "30% IN ADVANCE + 70% BEFORE SHIPMENT",
};

const num = (v) => Number(v || 0);

/* DatePicker ต้องได้ dayjs เท่านั้น และค่าจาก DB อาจเป็น 0000-00-00 (วันว่าง)
   ซึ่ง dayjs จะ parse เป็นปี 1899 ถ้าไม่กรองออก */
const toDay = (v) => {
  if (!v) return null;
  const d = dayjs(v);
  if (!d.isValid() || d.year() < 1970) return null;
  return d;
};
const fmtDay = (v) => (v && dayjs(v).isValid() ? dayjs(v).format("YYYY-MM-DD") : null);

/* น้ำหนักสุทธิ/รวม + ขนาดบรรจุ ดึงจาก Packing Goods (er_pg) เป็น "ค่าตั้งต้น" ตอนสร้างเอกสาร
   ถ้าใน PG ไม่ได้กรอกไว้ ช่องจะว่าง แล้วผู้ใช้พิมพ์เองในตารางสินค้าได้
   (เดิมล็อกไว้ทั้งหมด พอ PG ไม่มีข้อมูล ใบกำกับเลยไม่มีน้ำหนักและกรอกไม่ได้) */
const applyWeightsFromPG = async (rows) => {
  if (!rows.some((r) => r.pg_code)) return rows;
  try {
    const res = await optionService.optionsPG({ p: "cus" });
    const byCode = new Map((res.data?.data || []).map((p) => [p.pg_code, p]));
    return rows.map((row) => {
      const p = byCode.get(row.pg_code);
      if (!p) return row;
      /* ประเภทหน่วยบรรจุกำหนดน้ำหนักพาเลตที่ต้องบวกเข้า Gross Weight
         พาเลตไม้ 21 kg / พาเลตพลาสติก 7.8 kg / กล่อง 0 kg ต่อ 1 หน่วย */
      const packType = p.pack_type || "CARTON";
      return {
        ...row,
        net_wt: num(p.net_wt) > 0 ? num(p.net_wt) : num(row.net_wt),
        gross_wt: num(p.gross_wt) > 0 ? num(p.gross_wt) : num(row.gross_wt),
        unit_cs: num(row.unit_cs) > 0 ? num(row.unit_cs) : num(p.unit_cs),
        pack_type: packType,
        pallet_wt: palletWeightOf(packType),
        /* หน่วยขายตั้งต้นตามชนิดหน่วยบรรจุ (พาเลต -> PALLET, กล่อง -> CASE) */
        sale_unit: row.sale_unit || saleUnitOf(packType),
        /* ค่าที่ผู้ใช้พิมพ์ไว้แล้วมาก่อนเสมอ PG เป็นแค่ค่าตั้งต้น */
        packing_size: row.packing_size || p.packing_size || "",
      };
    });
  } catch {
    /* ดึงไม่ได้ก็ใช้ค่าที่ prefill มาให้ ไม่ต้องขัดจังหวะผู้ใช้ */
    return rows;
  }
};

const MyManage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = location.state || {};
  const isCreate = config?.action === "create";
  /* โหมดดูอย่างเดียว — ปิดการแก้ไขทั้งฟอร์ม ซ่อนปุ่มบันทึก/ลบแถว */
  const isView = config?.action === "view";

  const [form] = Form.useForm();
  const [detail, setDetail] = useState([]);
  const [packingSizeOptions, setPackingSizeOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState("USD");
  const [delcodes, setDelcodes] = useState([]);
  const [jocodes, setJocodes] = useState([]);
  const [openJoModal, setOpenJoModal] = useState(false);
  const [invBanks, setInvBanks] = useState([]);
  const [openBanks, setOpenBanks] = useState(false);
  const [invCode, setInvCode] = useState(null);
  const [consignees, setConsignees] = useState([]);

  /* สร้างจาก Proforma Invoice */
  const [piCode, setPiCode] = useState(null);
  const [piReserved, setPiReserved] = useState(false);
  const [openPiModal, setOpenPiModal] = useState(false);
  const [openMapModal, setOpenMapModal] = useState(false);

  /* โหลดรายชื่อ consignee ของลูกค้า แล้วเลือกตัว default ให้ถ้ายังไม่เคยเลือก */
  const loadConsignees = async (cuscode, autoSelect = false) => {
    if (!cuscode) return;
    try {
      const res = await consigneeService.list(cuscode);
      const list = res.data?.data || [];
      setConsignees(list);
      if (autoSelect && list.length > 0) {
        const def = list.find((c) => Number(c.is_default) === 1) || list[0];
        applyConsignee(def.id, list);
      }
    } catch {
      setConsignees([]);
    }
  };

  /* เลือก consignee → เก็บสำเนาข้อมูลลงฟอร์ม (เอกสารเก่าต้องไม่เปลี่ยนตาม master) */
  const applyConsignee = (id, list = consignees) => {
    const c = list.find((x) => String(x.id) === String(id));
    if (!c) return;
    form.setFieldsValue({
      consignee_id: c.id,
      consignee_name: c.consignee_name,
      consignee_address: c.address,
      consignee_contact: c.contact_name,
      consignee_email: c.email,
      consignee_tel: c.tel,
    });
  };

  const handleBanksRemove = (record) => {
    setInvBanks((prev) => prev.filter((item) => item?.acc_no !== record?.acc_no));
  };


  const deleteDetail = (seq) => {
    setDetail((prev) => prev.filter((d) => d.seq !== seq).map((d, i) => ({ ...d, seq: i + 1 })));
  };

  const updateDetail = (index, field, value) => {
    setDetail((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  /* ---- prefill จาก Job Order (รองรับหลายใบ) ---- */
  const loadFromJobOrders = async (list) => {
    const codes = (Array.isArray(list) ? list : [list]).filter(Boolean);
    if (codes.length === 0) {
      message.warning("กรุณาเลือก Job Order");
      return;
    }
    try {
      setLoading(true);
      const res = await invService.prefillJo(codes);
      const { header, detail: det } = res.data?.data || {};
      if (!header) {
        message.warning("ไม่พบ Job Order");
        return;
      }
      setJocodes(header.jocodes || codes);
      setDelcodes([]);
      form.setFieldsValue({
        delcode: header.delcode || "",
        jocode: header.jocodes_list || header.jocode,
        cuscode: header.cuscode,
        cusname: header.cusname,
        brand: header.brand,
        customer_po: header.pi_no,
        ship_via: header.ship_via,
        shipping_mark: header.shipping_mark || header.brand || "",
        etd: toDay(header.etd),
        eta: toDay(header.eta),
        inv_date: dayjs(),
        currency: header.currency || "USD",
        ...DEFAULT_TERMS,
      });
      setCurrency(header.currency || "USD");
      const rows = (det || []).map((d, i) => ({
        seq: i + 1,
        pg_code: d.pg_code,
        pg_name: d.pg_ivname || d.pg_name,
        hs_code: d.pg_code,
        packing_size: d.packing_size || "",
        net_wt: num(d.net_wt),
        gross_wt: num(d.gross_wt),
        unit_cs: num(d.unit_cs),
        carton: num(d.carton),
        /* จำนวนขายเริ่มต้น = จำนวนกล่อง ส่วนหน่วยขายให้ applyWeightsFromPG
           ตั้งตามชนิดหน่วยบรรจุของสินค้า (กล่อง -> CASE, พาเลต -> PALLET) */
        sale_qty: num(d.carton),
        brand: d.brand || header.brand || "",
        unit_price: num(d.unit_price),
      }));
      setDetail(await applyWeightsFromPG(rows));
      await loadConsignees(header.cuscode, true);
      message.success(`ดึงข้อมูลจาก Job Order สำเร็จ (${codes.length} ใบ)`);
    } catch (err) {
      message.error(err?.response?.data?.message || "ดึงข้อมูลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  /* ---- prefill จาก Proforma Invoice ---- */
  const loadFromProforma = async (pi) => {
    if (!pi?.pi_code) return;
    try {
      setLoading(true);
      const res = await invService.prefillPi(pi.pi_code);
      const { header, detail: det } = res.data?.data || {};
      if (!header) {
        message.warning("ไม่พบ Proforma Invoice");
        return;
      }
      setPiCode(header.pi_code);
      setPiReserved(Number(header.inv_reserved) === 1);
      setInvCode(null);
      setJocodes([]);
      setDelcodes([]);
      setCurrency(header.currency || "USD");
      form.setFieldsValue({
        ...header,
        inv_code: "",
        inv_date: header.inv_date ? dayjs(header.inv_date) : dayjs(),
        shipping_mark: header.shipping_mark || header.brand || "",
        etd: toDay(header.etd),
        eta: toDay(header.eta),
      });
      setDetail(
        await applyWeightsFromPG(
          (det || []).map((d, i) => ({
            seq: i + 1,
            pg_code: d.pg_code || null,
            pg_name: d.pg_name || "",
            pi_pg_name: d.pi_pg_name || d.pg_name || "",
            hs_code: d.hs_code || "",
            packing_size: d.packing_size || "",
            net_wt: num(d.net_wt),
            gross_wt: num(d.gross_wt),
            unit_cs: num(d.unit_cs),
            carton: num(d.carton),
            sale_qty: num(d.sale_qty) > 0 ? num(d.sale_qty) : num(d.carton),
            sale_unit: d.sale_unit || "",
            brand: d.brand || header.brand || "",
            unit_price: num(d.unit_price),
          }))
        )
      );
      if (header.cuscode) await loadConsignees(header.cuscode);

      const unmapped = (det || []).filter((d) => !d.pg_code).length;
      if (unmapped > 0) {
        message.warning(`มี ${unmapped} รายการที่ยังไม่มีรหัสสินค้า ต้องจับคู่ก่อนบันทึก`);
        setOpenMapModal(true);
      } else {
        message.success(`ดึงข้อมูลจาก ${header.pi_code} สำเร็จ`);
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "ดึงข้อมูลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  /* ผลลัพธ์การจับคู่สินค้า : [{ index, pg_code, pg_name, unit_cs }] */
  const applyProductMapping = (mapped) => {
    setDetail((prev) =>
      prev.map((row, i) => {
        const m = mapped.find((x) => x.index === i);
        if (!m) return row;
        return {
          ...row,
          pg_code: m.pg_code,
          pg_name: m.pg_name || row.pg_name,
          unit_cs: row.unit_cs || num(m.unit_cs),
          /* น้ำหนัก/ขนาดบรรจุ ยึดจาก Packing Goods ที่จับคู่ ถ้า PG ไม่ได้กรอกไว้จึงใช้ค่าเดิม */
          net_wt: num(m.net_wt) > 0 ? num(m.net_wt) : num(row.net_wt),
          gross_wt: num(m.gross_wt) > 0 ? num(m.gross_wt) : num(row.gross_wt),
          packing_size: row.packing_size || m.packing_size || "",
        };
      })
    );
  };

  const handleSelectJobOrder = (record) => {
    setOpenJoModal(false);
    loadFromJobOrders([record?.jocode]);
  };

  /* ---- โหลดข้อมูลเดิมเมื่อ edit ---- */
  const loadInvoice = async (code) => {
    try {
      setLoading(true);
      const res = await invService.get(code);
      const { header, detail: det } = res.data?.data || {};
      if (!header) return;
      setInvCode(header.inv_code);
      setDelcodes(header.delcodes || (header.delcode ? header.delcode.split(",").map((s) => s.trim()) : []));
      setInvBanks(header.banks || []);
      setCurrency(header.currency || "USD");
      form.setFieldsValue({
        ...header,
        inv_date: header.inv_date ? dayjs(header.inv_date) : null,
        etd: toDay(header.etd),
        eta: toDay(header.eta),
      });
      setDetail(
        (det || []).map((d, i) => ({
          seq: i + 1,
          pg_code: d.pg_code,
          pg_name: d.pg_name,
          hs_code: d.hs_code,
          packing_size: d.packing_size || "",
          net_wt: num(d.net_wt),
          gross_wt: num(d.gross_wt),
          unit_cs: num(d.unit_cs),
          carton: num(d.carton),
          sale_qty: num(d.sale_qty) > 0 ? num(d.sale_qty) : num(d.carton),
          sale_unit: d.sale_unit || "CASE",
          brand: d.brand || header.brand || "",
          /* น้ำหนักพาเลตที่บันทึกไว้ตอนออกเอกสาร ไม่คำนวณใหม่
             ถ้าวันหลังแก้ค่าคงที่ ใบเก่าจะยังพิมพ์เท่าเดิม */
          pallet_wt: num(d.pallet_wt),
          unit_price: num(d.unit_price),
        }))
      );
      loadConsignees(header.cuscode);
    } catch {
      message.error("โหลดข้อมูลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  /* ตัวเลือกขนาดบรรจุ ดึงรายการที่มีอยู่จริงใน Packing Goods มาให้เลือก
     ผู้ใช้ยังพิมพ์ค่าที่ไม่มีในลิสต์เองได้ (ช่องเป็น AutoComplete ไม่ใช่ Select) */
  useEffect(() => {
    optionService
      .optionsPG({ p: "cus" })
      .then((res) => {
        const list = res.data?.data || [];
        setPackingSizeOptions(
          [...new Set(list.map((p) => String(p.packing_size || "").trim()).filter(Boolean))].sort()
        );
      })
      .catch(() => setPackingSizeOptions([]));
  }, []);

  useEffect(() => {
    if (!isCreate && config?.code) {
      loadInvoice(config.code);
    } else if (isCreate && config?.jocode) {
      loadFromJobOrders([config.jocode]);
    }
  }, []);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (detail.length === 0) {
        message.warning("ไม่มีรายการสินค้า");
        return;
      }
      /* Invoice ต้องมีรหัสสินค้าครบทุกแถว — ใบที่ดึงมาจาก PI ต้องจับคู่ให้ครบก่อน */
      const unmapped = detail.filter((d) => !String(d.pg_code || "").trim()).length;
      if (unmapped > 0) {
        message.warning(`ยังมี ${unmapped} รายการที่ไม่มีรหัสสินค้า กรุณาจับคู่ให้ครบก่อนบันทึก`);
        setOpenMapModal(true);
        return;
      }
      const detailPayload = detail.map((d) => {
        const total_qty = num(d.unit_cs) * num(d.carton);
        /* ยอดเงินคิดจาก "จำนวนหน่วยขาย" ที่แสดงในคอลัมน์ QUANTITY ของใบกำกับ
           ไม่ใช่จำนวนชิ้นรวม (unit_cs x carton) ซึ่งเป็นหน่วยเก็บ */
        const sale_qty = num(d.sale_qty) > 0 ? num(d.sale_qty) : num(d.carton);
        const line_total = sale_qty * num(d.unit_price);
        return {
          ...d,
          total_qty,
          sale_qty,
          sale_unit: d.sale_unit || "CASE",
          /* เก็บน้ำหนักพาเลตที่ใช้ตอนออกเอกสารไว้ด้วย ใบเก่าจะได้ไม่เพี้ยน
             ถ้าวันหลังมีการแก้ค่าคงที่ของพาเลต */
          pallet_wt: num(d.pallet_wt),
          line_total,
        };
      });
      const payload = {
        header: {
          ...values,
          /* ตอนสร้าง ใช้เลขที่ผู้ใช้พิมพ์ (ว่าง = ให้ระบบรันเลข)
             เดิมส่ง invCode ซึ่งเป็น null ตอนสร้าง เลขที่กรอกเองจึงถูกทิ้งไป */
          inv_code: isCreate ? String(values.inv_code || "").trim() : invCode,
          pi_code: piCode,
          delcodes,
          inv_date: values.inv_date ? dayjs(values.inv_date).format("YYYY-MM-DD") : null,
          /* DatePicker คืนค่าเป็น dayjs ต้องแปลงเป็น YYYY-MM-DD ก่อนส่ง
             ไม่งั้น MySQL รับไม่ได้แล้วกลายเป็นวันว่าง */
          etd: fmtDay(values.etd),
          eta: fmtDay(values.eta),
          // รวมน้ำหนักจาก PG master × จำนวนลัง
          net_weight: totalNetWeight || null,
          gross_weight: totalGrossWeight || null,
          // ค่าระวาง / ค่าประกันภัย : ว่างหรือ 0 = ไม่พิมพ์บรรทัดนั้นในใบ Invoice
          // ทั้งสองช่องใช้สกุลเงินเดียวกับราคาขายสินค้า ไม่ต้องแปลงค่า
          freight_amount: num(values.freight_amount) > 0 ? num(values.freight_amount) : null,
          insurance_amount: num(values.insurance_amount) > 0 ? num(values.insurance_amount) : null,
          freight_term:
            num(values.freight_amount) > 0 || num(values.insurance_amount) > 0
              ? (values.freight_term || (num(values.insurance_amount) > 0 ? "CIF" : "CFR"))
              : null,
        },
        detail: detailPayload,
        banks: invBanks,
      };
      if (isCreate) {
        await invService.create(payload);
        message.success("บันทึกสำเร็จ");
      } else {
        await invService.update(payload);
        message.success("อัพเดทสำเร็จ");
      }
      navigate(-1);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || "บันทึกไม่สำเร็จ");
    }
  };

  const totalAmount = detail.reduce(
    (s, d) =>
      s + (num(d.sale_qty) > 0 ? num(d.sale_qty) : num(d.carton)) * num(d.unit_price),
    0
  );
  const totalCases = detail.reduce((s, d) => s + num(d.carton), 0);
  const totalNetWeight = detail.reduce((s, d) => s + num(d.carton) * num(d.net_wt), 0);
  /* Gross Weight รวม = จำนวนกล่อง x (น้ำหนักรวมต่อกล่อง + น้ำหนักพาเลตต่อหน่วย)
     Net Weight ไม่บวกพาเลต เพราะเป็นน้ำหนักสินค้าล้วนตามหลักสากล */
  const totalGrossWeight = detail.reduce(
    (s, d) => s + num(d.carton) * (num(d.gross_wt) + num(d.pallet_wt)),
    0
  );

  const columns = detailColumns({
    updateDetail,
    deleteDetail,
    currency,
    packingSizeOptions,
    readOnly: isView,
  });
  const bankColumns = invoiceBankColumns({ removeBank: handleBanksRemove, readOnly: isView });

  return (
    <div className="invoice-manage">
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        <Flex justify="space-between" align="center">
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            {config?.title || "Invoice"}
          </Typography.Title>
          <Space>
            <Button size="small" className="bn-action" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              ย้อนกลับ
            </Button>
            {!isView && (
              <Button type="primary" size="small" className="bn-action" icon={<SaveOutlined />} onClick={handleSave}>
                บันทึก
              </Button>
            )}
          </Space>
        </Flex>

        {isCreate && (
          <Card size="small" title="ดึงข้อมูลจาก Job Order หรือ Proforma Invoice" loading={loading}>
            <Flex gap={8} align="center" wrap="wrap">
              <Button type="primary" icon={<FileSearchOutlined />} onClick={() => setOpenJoModal(true)}>
                เลือก Job Order
              </Button>
              <Button icon={<FileSearchOutlined />} onClick={() => setOpenPiModal(true)}>
                เลือก Proforma Invoice
              </Button>
              {piCode && (
                <Typography.Text>
                  ดึงจาก <strong>{piCode}</strong>
                  {piReserved ? " (ใช้เลข Invoice ที่จองไว้)" : ""}
                </Typography.Text>
              )}
              {jocodes.length > 0 ? (
                <Typography.Text>
                  เลือก Job Order <strong>{jocodes.join(", ")}</strong>
                </Typography.Text>
              ) : (
                <Typography.Text type="secondary">ยังไม่ได้เลือก Job Order</Typography.Text>
              )}
            </Flex>
          </Card>
        )}

        <Card loading={loading} styles={{ body: { paddingTop: 8 } }}>
          <Form
            form={form}
            layout="vertical"
            autoComplete="off"
            disabled={isView}
            onValuesChange={(ch) => { if (ch.currency) setCurrency(ch.currency); }}
          >
            {/* ฟิลด์ซ่อน — ต้องอยู่นอก Tabs เพื่อให้ลงทะเบียนกับฟอร์มเสมอ */}
            <Form.Item name="delcode" hidden><Input /></Form.Item>
            <Form.Item name="cuscode" hidden><Input /></Form.Item>

            <Tabs
              defaultActiveKey="main"
              items={[
                {
                  key: "main",
                  label: "ข้อมูลหลัก",
                  forceRender: true,
                  children: (
                    <Row gutter={[16, 0]}>
                      {/* ตอนสร้าง : เว้นว่าง = ระบบรันเลขให้ / กรอกเอง = ใช้เลขที่กรอก
                          ตอนแก้ไข : ล็อก เพราะเลขที่ผูกกับ PI และรายการอื่นไปแล้ว */}
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item
                          label="เลขที่ Invoice"
                          name="inv_code"
                          tooltip={
                            isCreate
                              ? "เว้นว่างไว้ = ระบบออกเลขให้อัตโนมัติ ถ้าต้องการกำหนดเองให้พิมพ์เลขที่ต้องการ"
                              : "แก้ไขเลขที่เอกสารไม่ได้"
                          }
                        >
                          <Input
                            disabled={!isCreate}
                            maxLength={30}
                            showCount={isCreate}
                            placeholder="เว้นว่าง = ระบบออกเลขให้"
                            onChange={(e) =>
                              form.setFieldValue("inv_code", e.target.value.toUpperCase())
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Job Order" name="jocode"><Input disabled /></Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item
                          label="วันที่ Invoice"
                          name="inv_date"
                          rules={[{ required: true, message: "กรุณาเลือกวันที่" }]}
                        >
                          <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item label="สกุลเงิน" name="currency">
                          <Select
                            options={["USD", "EUR", "THB", "SGD", "GBP", "JPY"].map((c) => ({ label: c, value: c }))}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12} md={9}>
                        <Form.Item label="ลูกค้า" name="cusname"><Input disabled /></Form.Item>
                      </Col>
                      {/* Brand ระดับเอกสาร ใช้เป็นค่าตั้งต้นของแบรนด์รายบรรทัดเท่านั้น
                          แบรนด์ที่พิมพ์จริงอยู่หน้าชื่อสินค้าแต่ละรายการในตารางด้านล่าง */}
                      <Col xs={24} sm={12} md={5}>
                        <Form.Item
                          label="Brand (ค่าตั้งต้น)"
                          name="brand"
                          tooltip="ใช้เติมให้รายการสินค้าที่ยังไม่ได้ระบุแบรนด์ แบรนด์จริงแก้ได้ในตารางสินค้า"
                        >
                          <Input placeholder="เช่น TIGER KHAN" />
                        </Form.Item>
                      </Col>
                      {/* MARK = เครื่องหมายบนหีบห่อ/พาเลต ไม่ใช่แบรนด์สินค้า (ข้อ 3) */}
                      <Col xs={24} sm={12} md={5}>
                        <Form.Item
                          label="Shipping Mark"
                          name="shipping_mark"
                          tooltip="เครื่องหมายที่พิมพ์ข้างกล่อง/พาเลต พิมพ์ในช่อง MARK ของใบกำกับ มีหลาย mark ให้กด Enter ขึ้นบรรทัดใหม่ 1 บรรทัดต่อ 1 mark"
                        >
                          <Input.TextArea autoSize={{ minRows: 1, maxRows: 5 }} placeholder={"เช่น FRICODAN SA\n(หลาย mark กด Enter ขึ้นบรรทัดใหม่)"} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={5}>
                        <Form.Item label="Customer PO Ref" name="customer_po">
                          <Input placeholder="เช่น SG-24-01" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={5}>
                        <Form.Item label="Country of Origin" name="country_of_origin"><Input /></Form.Item>
                      </Col>

                      <Col xs={24}>
                        <Form.Item label="หมายเหตุ" name="remark">
                          <Input placeholder="หมายเหตุ" />
                        </Form.Item>
                      </Col>
                    </Row>
                  ),
                },
                {
                  key: "consignee",
                  label: "ผู้รับสินค้า",
                  forceRender: true,
                  children: (
                    <>
                      <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                          <Form.Item label="เลือก Consignee" name="consignee_id">
                            <Select
                              allowClear
                              showSearch
                              optionFilterProp="label"
                              placeholder="เลือกผู้รับสินค้า"
                              onChange={(v) => applyConsignee(v)}
                              options={consignees.map((c) => ({
                                value: c.id,
                                label:
                                  c.consignee_name +
                                  (Number(c.is_default) === 1 ? " (ค่าเริ่มต้น)" : ""),
                              }))}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item label="ชื่อผู้รับสินค้า" name="consignee_name"><Input /></Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item label="ผู้ติดต่อ" name="consignee_contact"><Input /></Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item label="ที่อยู่" name="consignee_address">
                            <Input.TextArea rows={2} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item label="Email" name="consignee_email"><Input /></Form.Item>
                        </Col>
                        <Col xs={24}>
                          <Form.Item
                            label="Notify party"
                            name="notify_party"
                            tooltip="เว้นว่าง = ใช้ข้อมูล Consignee เดียวกัน"
                          >
                            <Input.TextArea rows={2} placeholder="เว้นว่าง = ใช้ Consignee เดียวกัน" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item label="โทรศัพท์" name="consignee_tel"><Input /></Form.Item>
                        </Col>
                      </Row>
                    </>
                  ),
                },
                {
                  key: "shipping",
                  label: "ขนส่ง & เงื่อนไข",
                  forceRender: true,
                  children: (
                    <>
                      <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item label={<span><span className="inv-dot">●</span> Port of Loading</span>} name="port_of_loading">
                            <Input placeholder="เช่น LAEM CHABANG" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item label={<span><span className="inv-dot">●</span> Place of receipt</span>} name="place_of_receipt">
                            <Input placeholder="เช่น SINGAPORE" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item label={<span><span className="inv-dot">●</span> Final destination</span>} name="final_destination">
                            <Input placeholder="เช่น SINGAPORE" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item label={<span><span className="inv-dot">●</span> Feeder &amp; Ocean Vessel</span>} name="carrier">
                            <Input placeholder="เช่น WHUTTHI BHUM V.038S" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item
                            label={<span><span className="inv-dot">●</span> บรรทัดรวมยอด</span>}
                            name="fob_term"
                            tooltip="ข้อความในช่องรวมยอดท้ายตาราง เช่น TOTAL FOB LAEM CHABANG"
                          >
                            <Input placeholder="เช่น TOTAL FOB LAEM CHABANG" />
                          </Form.Item>
                        </Col>

                        {/* ETD/ETA ดึงจาก Job Order มาเป็นค่าตั้งต้น แต่แก้เป็นวันจริงได้
                            ต้องเป็น DatePicker ไม่ใช่ Input ธรรมดา ไม่งั้นค่าที่พิมพ์จะไม่ใช่วันที่จริง
                            แล้วลง DB เป็น 0000-00-00 (ใบพิมพ์จะโชว์ 30/11/1899) */}
                        <Col xs={12} sm={12} md={6}>
                          <Form.Item label="ETD" name="etd" tooltip="ค่าตั้งต้นจาก Job Order แก้เป็นวันจริงได้">
                            <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={12} md={6}>
                          <Form.Item label="ETA" name="eta" tooltip="ค่าตั้งต้นจาก Job Order แก้เป็นวันจริงได้">
                            <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item label="Ship Via" name="ship_via" tooltip="วิธีขนส่ง พิมพ์ในใบ Invoice ถ้ากรอก เช่น FCL 1X20', LCL, BY AIR">
                            <Input placeholder="เช่น FCL 1X20' / LCL / BY AIR" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item label="Volume (CBM)" name="volume_cbm" tooltip="ปริมาตรรวม (ลูกบาศก์เมตร) พิมพ์ในใบ Invoice ถ้ากรอก">
                            <Input placeholder="เว้นว่างได้" />
                          </Form.Item>
                        </Col>

                        {/* ค่าระวาง — ใส่เมื่อขายราคาต่อหน่วยเป็น CFR/CIF
                            ถ้าเว้นว่างหรือเป็น 0 จะไม่พิมพ์ 2 บรรทัดนี้ในใบ Invoice */}
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item
                            label={`ค่าระวาง FREIGHT (${currency || "USD"})`}
                            name="freight_amount"
                            tooltip={`ใช้สกุลเงินเดียวกับราคาขายสินค้า (${currency || "USD"}) เว้นว่าง = ไม่พิมพ์บรรทัด FREIGHT`}
                          >
                            <InputNumber min={0} step={0.01} controls={false} style={{ width: "100%" }} placeholder="เว้นว่างได้" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item
                            label={`ค่าประกันภัย INSURANCE (${currency || "USD"})`}
                            name="insurance_amount"
                            tooltip={`ใช้สกุลเงินเดียวกับราคาขายสินค้า (${currency || "USD"}) กรอกเมื่อขายเทอม CIF เว้นว่าง = ไม่พิมพ์บรรทัด INSURANCE`}
                          >
                            <InputNumber min={0} step={0.01} controls={false} style={{ width: "100%" }} placeholder="เว้นว่างได้" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item
                            label="เงื่อนไขยอดรวมหลังบวกค่าใช้จ่าย"
                            name="freight_term"
                            tooltip="ใช้เป็นป้ายบรรทัดสุดท้าย เช่น CFR SINGAPORE / CIF SINGAPORE"
                          >
                            <Select
                              allowClear
                              placeholder="CFR"
                              options={[
                                { value: "CFR", label: "CFR" },
                                { value: "CIF", label: "CIF" },
                              ]}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                          <Form.Item label="Delivery Term" name="delivery_term"><Input /></Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item label="Payment Term" name="payment_term"><Input /></Form.Item>
                        </Col>
                      </Row>
                    </>
                  ),
                },
                {
                  key: "bank",
                  label: `ธนาคาร${invBanks.length ? ` (${invBanks.length})` : ""}`,
                  forceRender: true,
                  children: (
                    <>
                      <Flex justify="flex-end" className="!mb-2">
                        <Button
                          size="small"
                          className="bn-success-outline bn-center"
                          icon={<BankFilled />}
                          onClick={() => setOpenBanks(true)}
                        >
                          เลือกธนาคาร
                        </Button>
                      </Flex>
                      <Table
                        columns={bankColumns}
                        dataSource={invBanks}
                        locale={{ emptyText: <span>ยังไม่ได้เลือกธนาคาร</span> }}
                        size="small"
                        rowKey="acc_no"
                        pagination={false}
                        scroll={{ x: "max-content" }}
                      />
                    </>
                  ),
                },
              ]}
            />
          </Form>
        </Card>

        <Card
          title={`รายการสินค้า (${detail.length})`}
          extra={
            <Space size="large" wrap>
              <Typography.Text type="secondary">
                รวมลัง <strong>{totalCases.toLocaleString()}</strong>
              </Typography.Text>
              <Typography.Text type="secondary">
                NET <strong>{totalNetWeight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> KG
              </Typography.Text>
              <Typography.Text type="secondary">
                GROSS <strong>{totalGrossWeight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> KG
              </Typography.Text>
            </Space>
          }
        >
          <Table
            size="small"
            rowKey="seq"
            columns={columns}
            dataSource={detail}
            pagination={false}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: <span>ยังไม่มีรายการ — กด "เลือก Job Order" ด้านบนเพื่อดึงข้อมูล</span> }}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={10} align="right">
                  <Typography.Text strong>ยอดรวมทั้งสิ้น ({currency})</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Typography.Text strong>
                    {totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} />
              </Table.Summary.Row>
            )}
          />
        </Card>
      </Space>

      <ModalPickPI open={openPiModal} close={setOpenPiModal} confirm={loadFromProforma} />
      <ModalMapProducts
        open={openMapModal}
        close={setOpenMapModal}
        rows={detail}
        confirm={applyProductMapping}
      />

      {openJoModal && (
        <ModalJobOrder
          show={openJoModal}
          close={() => setOpenJoModal(false)}
          selected={handleSelectJobOrder}
        />
      )}

      {openBanks && (
        <ModalBanks
          show={openBanks}
          close={() => setOpenBanks(false)}
          values={(v) => setInvBanks(v)}
          selected={invBanks}
        />
      )}
    </div>
  );
};

export default MyManage;
